import React, { useSyncExternalStore } from 'react';
import { analyticsConsent } from '../../lib/analyticsConsent';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { Box, Typography, Link, Stack, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaInstagram, FaYoutube } from 'react-icons/fa';
import logo from '../../assets/logo_white.png';
import theme from '../../theme';

const Footer = ({ analyticsTrigger, onAnalyticsOpen }) => {
  const { locale } = useWebLocale();
  const navigate = useNavigate();
  const analyticsChoice = useSyncExternalStore(
    analyticsConsent.subscribe,
    analyticsConsent.getSnapshot,
  );

  const handleTermsClick = () => {
    navigate('/about/terms-of-use');
  };

  const handlePrivacyClick = () => {
    navigate('/about/privacy-policy');
  };

  const handleSponsorshipClick = () => {
    navigate('/sponsorship');
  };

  const handleInstagramClick = () => {
    window.open('https://www.instagram.com/postpop.kr', '_blank');
  };

  const handleYoutubeClick = () => {
    window.open('https://www.youtube.com/@포스트팝', '_blank');
  };

  const linkSx = {
    px: { xs: 'clamp(9px, 3vw, 12px)', sm: 1.75, md: 2 },
    py: { xs: 0.75, md: 1 },
    fontSize: { xs: 11, sm: 12, md: 13, lg: 14 },
    color: theme.palette.white.opacity80,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const dividerSx = {
    width: '1px',
    height: '8px',
    backgroundColor: theme.palette.white.opacity50,
  };

  const socialLinkSx = {
    ...linkSx,
    px: { xs: 'clamp(9px, 3vw, 12px)', sm: 1.75, md: 2 },
    minWidth: { xs: 36, sm: 38, md: 32 },
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: 'var(--pp-footer-bg)',
        borderTop: '1px solid var(--pp-border)',
        display: 'flex',
        justifyContent: 'center',
        pt: { xs: 3.75, lg: 6.25 },
        pb: { xs: 3.75, lg: 6.25 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Stack
        spacing={{ xs: 2.5, lg: 3.75 }}
        alignItems={'center'}
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        {/* 로고 */}
        <Box
          component={'img'}
          src={logo}
          alt="logo"
          sx={{
            width: { xs: 77, md: 91 },
            objectFit: 'cover',
          }}
        />

        {/* 링크들 */}
        <Stack
          spacing={{ xs: 1, lg: 2.5 }}
          alignItems={'center'}
          sx={{ minWidth: 0, maxWidth: '100%' }}
        >
          <Stack
            direction="row"
            spacing={0}
            alignItems={'center'}
            sx={{ flexWrap: 'wrap', justifyContent: 'center', maxWidth: '100%' }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{ flexWrap: 'wrap', justifyContent: 'center', maxWidth: '100%' }}
            >
              <Link component="button" onClick={handleTermsClick} sx={linkSx}>
                {locale === 'en' ? 'Terms of Service' : '이용약관'}
              </Link>
              <Divider orientation="vertical" sx={dividerSx} />
              <Link component="button" onClick={handlePrivacyClick} sx={linkSx}>
                {locale === 'en' ? 'Privacy Policy' : '개인정보처리방침'}
              </Link>
              <Divider orientation="vertical" sx={dividerSx} />
              <Link component="button" onClick={handleSponsorshipClick} sx={linkSx}>
                {locale === 'en' ? 'Support Postpop' : '후원'}
              </Link>
            </Stack>
            <Divider orientation="vertical" sx={dividerSx} />
            <Stack direction="row" alignItems="center">
              <Link
                component="button"
                onClick={handleInstagramClick}
                sx={socialLinkSx}
                aria-label="Instagram"
              >
                <Box component={FaInstagram} sx={{ fontSize: { xs: 17, md: 18, lg: 19 } }} />
              </Link>
              <Divider orientation="vertical" sx={dividerSx} />
              <Link
                component="button"
                onClick={handleYoutubeClick}
                sx={socialLinkSx}
                aria-label="Youtube"
              >
                <Box component={FaYoutube} sx={{ fontSize: { xs: 18, md: 19, lg: 20 } }} />
              </Link>
            </Stack>
          </Stack>

          {analyticsConsent.enabled && analyticsChoice === 'unknown' && (
            <Link
              component="button"
              ref={analyticsTrigger}
              onClick={onAnalyticsOpen}
              sx={{ ...linkSx, minHeight: 32 }}
            >
              {locale === 'en' ? 'Website analytics settings' : '방문 통계 설정'}
            </Link>
          )}

          {/* 저작권 */}
          <Typography
            fontSize={{ xs: 11, md: 12, lg: 13 }}
            sx={{
              color: theme.palette.white.opacity50,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              width: '100%',
              textAlign: 'center',
            }}
          >
            Copyright © 포스트팝(postpop) All rights reserved.
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

export default Footer;
